/**
 * Pure time helpers for the timesheet row's inline fields.
 *
 * An entry stores `at` (its START, an ISO timestamp) and `minutes`; the backend keeps
 * no end. The end time and the duration box are two views of the same number, so each
 * helper here turns what the user typed into integer minutes (or `null` when it cannot
 * be read), and the row sends minutes.
 */

const MULTI_PART = /^(\d+):(\d{2})(?::(\d{2}))?$/;

/** The longest duration the row accepts: one day. An end time cannot express more. */
export const MAX_DURATION_MINUTES = 24 * 60;

/**
 * Reads a typed duration as whole minutes.
 *
 * Accepts `H:MM:SS` (seconds round to the nearest minute), `H:MM`, and a bare integer
 * (minutes). Minutes and seconds of 60 or more in a multi-part value are rejected, as
 * are empty or non-numeric input, a result of 0 or less, and one over 24 hours.
 *
 * @param {string} str The text typed into the duration box.
 * @returns {number|null} Integer minutes, or `null` when the input is not a duration.
 */
export function parseDuration(str) {
  const text = String(str ?? '').trim();
  if (!text) return null;

  let minutes;
  if (/^\d+$/.test(text)) {
    minutes = Number(text);
  } else {
    const parts = MULTI_PART.exec(text);
    if (!parts) return null;
    const [, h, m, s = '0'] = parts;
    if (Number(m) >= 60 || Number(s) >= 60) return null;
    minutes = Number(h) * 60 + Number(m) + Math.round(Number(s) / 60);
  }
  return Number.isSafeInteger(minutes) && minutes > 0 && minutes <= MAX_DURATION_MINUTES ? minutes : null;
}

/**
 * Minutes between an entry's start and an end clock time typed on the start's date.
 *
 * The end is the start's local date with the given `HH:MM`; an end earlier than the start
 * means it ran past midnight, so it moves to the next day. An end in the same minute as
 * the start is rejected rather than read as 24 hours.
 *
 * The start's seconds are ignored, because the end input shows `at + minutes` floored to
 * the minute: typing back the end the row displays must give back the same minutes.
 *
 * @param {string} atIso The entry's start timestamp.
 * @param {string} hhmm The end clock time, `HH:MM` (what `<input type="time">` yields).
 * @returns {number|null} Whole minutes, or `null` when the input is unusable or not positive.
 */
export function minutesFromEndTime(atIso, hhmm) {
  const start = new Date(atIso);
  const clock = /^(\d{2}):(\d{2})$/.exec(String(hhmm ?? '').trim());
  if (Number.isNaN(start.getTime()) || !clock) return null;
  const [h, m] = [Number(clock[1]), Number(clock[2])];
  if (h > 23 || m > 59) return null;

  if (h === start.getHours() && m === start.getMinutes()) return null;

  const base = new Date(start);
  base.setSeconds(0, 0);
  const end = new Date(base);
  end.setHours(h, m, 0, 0);
  if (end < base) end.setDate(end.getDate() + 1);

  const minutes = Math.round((end.getTime() - base.getTime()) / 60000);
  return minutes > 0 ? minutes : null;
}

/**
 * A date's local clock time as `HH:MM`, the value `<input type="time">` takes.
 *
 * @param {Date|string|number|null|undefined} date
 * @returns {string} `HH:MM`, or `''` when there is no usable date.
 */
export function toTimeInputValue(date) {
  const d = date == null ? null : new Date(date);
  if (!d || Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * When an entry ends: its start plus its minutes.
 *
 * @param {{at?: string, minutes?: number}} entry
 * @returns {Date|null} `null` when the entry has no usable start.
 */
export function endOf(entry) {
  const start = entry?.at ? new Date(entry.at) : null;
  if (!start || Number.isNaN(start.getTime())) return null;
  return new Date(start.getTime() + (Number(entry.minutes) || 0) * 60000);
}

/**
 * A date's local calendar day as `YYYY-MM-DD`, the value `<input type="date">` takes.
 *
 * @param {Date|string|number|null|undefined} date
 * @returns {string} `YYYY-MM-DD`, or `''` when there is no usable date.
 */
export function toDateInputValue(date) {
  const d = date == null ? null : new Date(date);
  if (!d || Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * An instant truncated to its minute. Durations are whole minutes and the row shows
 * minutes, so every comparison and every start edit is made on this basis: seconds never
 * decide whether two entries overlap or where an end falls.
 *
 * @param {number} ms Epoch milliseconds.
 * @returns {number} Epoch milliseconds with the seconds zeroed.
 */
export const floorToMinute = (ms) => Math.floor(ms / 60000) * 60000;

const CLOCK = /^(\d{2}):(\d{2})$/;

/** `HH:MM` → `{h, m}`, or `null` when it is not a clock time. */
const readClock = (hhmm) => {
  const parts = CLOCK.exec(String(hhmm ?? '').trim());
  if (!parts) return null;
  const [h, m] = [Number(parts[1]), Number(parts[2])];
  return h > 23 || m > 59 ? null : { h, m };
};

/** A local wall time on `day`'s calendar date, or `null` when the zone has no such time. */
const localTimeOn = (day, h, m) => {
  const t = new Date(day.getFullYear(), day.getMonth(), day.getDate(), h, m, 0, 0);
  return t.getHours() === h && t.getMinutes() === m ? t : null;
};

/**
 * The result of reading a typed start time: the new start and the duration it leaves,
 * or the reason it cannot be used.
 *
 * `nonexistent` is a wall time the zone skips (a spring-forward gap), which is not the
 * same fault as an unreadable or zero-length value and reads differently to a user.
 *
 * @typedef {{start: Date, minutes: number}|{error: 'invalid'|'nonexistent'}} StartEdit
 */

/**
 * Reads a typed start time, keeping the entry's end fixed.
 *
 * The end is `at + minutes` on the minute basis the end input shows. The new start is the
 * latest moment at or before that end whose clock reads the typed time, so a time later
 * than the end's clock means the day before (typing 23:50 on a 23:30–00:15 entry leaves a
 * 25-minute entry, typing 00:05 leaves 10). When a clock change makes the typed time occur
 * twice, the later occurrence before the end wins. A typed time equal to the end's is rejected
 * (it would be 0 minutes, or a whole day), and so is a result over 24 hours, which only a
 * day with a clock change can produce.
 *
 * The result's start has its seconds zeroed: the user typed a minute.
 *
 * @param {{at: string, minutes: number}} entry
 * @param {string} hhmm The typed start, `HH:MM`.
 * @returns {StartEdit}
 */
export function resolveStartEdit(entry, hhmm) {
  const clock = readClock(hhmm);
  const at = new Date(entry?.at);
  const minutes = Number(entry?.minutes);
  if (!clock || Number.isNaN(at.getTime()) || !(minutes > 0)) return { error: 'invalid' };

  const endMs = floorToMinute(at.getTime()) + minutes * 60000;
  const end = new Date(endMs);

  let start = localTimeOn(end, clock.h, clock.m);
  if (!start) return { error: 'nonexistent' };
  if (start.getTime() === endMs) return { error: 'invalid' };
  if (start.getTime() > endMs) {
    const dayBefore = new Date(end.getFullYear(), end.getMonth(), end.getDate() - 1, 12);
    start = localTimeOn(dayBefore, clock.h, clock.m);
    if (!start) return { error: 'nonexistent' };
  }

  // On a day whose clock goes back an hour the same wall time occurs twice. The latest
  // moment before the end is the one meant: 01:40-01:50 (the second 01:xx) with 01:30
  // typed is 01:30 the second time round, 20 minutes, not an hour and 20 minutes earlier.
  const repeat = new Date(start.getTime() + 3600000);
  if (repeat.getHours() === clock.h && repeat.getMinutes() === clock.m && repeat.getTime() < endMs) {
    start = repeat;
  }

  const resultMinutes = Math.round((endMs - start.getTime()) / 60000);
  if (resultMinutes <= 0 || resultMinutes > MAX_DURATION_MINUTES) return { error: 'invalid' };
  return { start, minutes: resultMinutes };
}

/**
 * Moves an entry's start to another calendar day, keeping its time of day (seconds
 * included), so the end moves with it and the duration is unchanged.
 *
 * @param {string} atIso The entry's start.
 * @param {string} day The target day, `YYYY-MM-DD`.
 * @returns {{start: Date}|{error: 'invalid'|'nonexistent'}} `nonexistent` when the entry's
 *   clock time does not exist on that day (a spring-forward gap).
 */
export function moveToDate(atIso, day) {
  const at = new Date(atIso);
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day ?? '').trim());
  if (Number.isNaN(at.getTime()) || !parts) return { error: 'invalid' };
  const [y, mo, d] = [Number(parts[1]), Number(parts[2]), Number(parts[3])];

  // setFullYear, not the Date constructor: the constructor reads years 0 to 99 as 19xx.
  const moved = new Date(at.getTime());
  moved.setFullYear(y, mo - 1, d);
  if (moved.getFullYear() !== y || moved.getMonth() !== mo - 1 || moved.getDate() !== d) {
    return { error: 'invalid' };
  }
  if (moved.getHours() !== at.getHours() || moved.getMinutes() !== at.getMinutes()) {
    return { error: 'nonexistent' };
  }
  return { start: moved };
}

/**
 * Reads a date, a start and an end typed together (the move dialog). Unlike
 * `resolveStartEdit` nothing is held fixed: the start is the date at the start clock, and
 * the end is the next time the clock reads the end time after it.
 *
 * @param {{day: string, start: string, end: string}} fields `YYYY-MM-DD`, `HH:MM`, `HH:MM`.
 * @returns {StartEdit}
 */
export function resolveWindow({ day, start, end }) {
  const date = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day ?? '').trim());
  const from = readClock(start);
  const to = readClock(end);
  if (!date || !from || !to) return { error: 'invalid' };

  const base = new Date(2000, 0, 1, 12);
  base.setFullYear(Number(date[1]), Number(date[2]) - 1, Number(date[3]));
  if (base.getMonth() !== Number(date[2]) - 1 || base.getDate() !== Number(date[3])) return { error: 'invalid' };

  const startAt = localTimeOn(base, from.h, from.m);
  if (!startAt) return { error: 'nonexistent' };
  let endAt = localTimeOn(base, to.h, to.m);
  if (!endAt) return { error: 'nonexistent' };
  if (endAt.getTime() === startAt.getTime()) return { error: 'invalid' };
  if (endAt.getTime() < startAt.getTime()) {
    const next = new Date(base.getFullYear(), base.getMonth(), base.getDate() + 1, 12);
    endAt = localTimeOn(next, to.h, to.m);
    if (!endAt) return { error: 'nonexistent' };
  }

  const minutes = Math.round((endAt.getTime() - startAt.getTime()) / 60000);
  if (minutes <= 0 || minutes > MAX_DURATION_MINUTES) return { error: 'invalid' };
  return { start: startAt, minutes };
}
